import { useState, useEffect, useImperativeHandle, forwardRef, useCallback, useMemo, useRef } from 'react'
import { configApi, PASSWORD_MASK } from '@/services/api/config'
import { apiClient } from '@/services/api/client'
import { FloatInput } from '@/components/ui/FloatInput'
import type { ConfigField, ConfigSubgroup, PermissionState } from '@/services/api/config'
import { RadarZoneEditor } from '@/components/config/RadarZoneEditor'
import type { RadarZones } from '@/components/config/RadarZoneEditor'
import { StructuredListEditor } from '@/components/config/StructuredListEditor'
import type { StructuredListField } from '@/components/config/StructuredListEditor'
import { DirectorDelegationEditor } from '@/components/config/DirectorDelegationEditor'
import { AbsenceTypeEditor } from '@/components/config/AbsenceTypeEditor'
import { DirectorPermissionsEditor } from '@/components/config/DirectorPermissionsEditor'
import { TagInput } from '@/components/config/TagInput'
import { FieldDelegationControl } from '@/components/config/FieldDelegationControl'
import { ColorField } from '@/components/config/ColorField'
import { RadarFilterDefaultEditor } from '@/components/config/RadarFilterDefaultEditor'
import { UserMultiSelect } from '@/components/config/UserMultiSelect'
import { DesignPresetPicker, DesignLogoUpload, DesignPreview } from '@/components/config/DesignConfigWidgets'
import { getPreset } from '@/utils/designPresets'

function asBool(v: unknown): boolean {
  return v === true || v === 'true' || v === 1
}

function displayValue(val: unknown, fallback: string = '—'): string {
  if (val === null || val === undefined) return fallback
  return String(val)
}

const CRITICAL_KEYS = [
  'priority_system_enabled',
  'allow_automatic_mode',
]

// Fields whose editors render as wide panels (e.g. radar default filter).
// Their rows are stacked vertically (label + explanation first, editor below)
// instead of being squeezed into a narrow side column next to the text.
const STACKED_FIELD_KEYS = new Set([
  'radar_filter_default_state',
  'no_substitute_recipient_user_ids',
  // Direktorkonfig v2: eigene Karte «Abwesenheitsarten» (ersetzt director_document_rules)
  'absence_types',
])

export interface ConfigEditorHandle {
  save: () => Promise<void>
  reset: () => void
  isDirty: () => boolean
}

export interface ConfigEditorProps {
  configType: string
  onDirtyChange?: (isDirty: boolean, dirtyKeys: Set<string>) => void
  onError?: (error: string | null) => void
  onSuccess?: () => void
  onConfigLoaded?: (label: string, description: string) => void
  hideDelegation?: boolean
  hideChrome?: boolean
  forcedSubgroup?: string
  apiBase?: string
  /** Direktorkonfig v2 (6.3): serverseitige per-Direktor-Rechte — überschreibt/
   * ergänzt die aus der Config-Response gelesenen field_permissions. */
  fieldPermissions?: Record<string, { visible: boolean; editable: boolean }>
}

export const ConfigEditor = forwardRef<ConfigEditorHandle, ConfigEditorProps>(function ConfigEditor(
  { configType, onDirtyChange, onError, onSuccess, onConfigLoaded, hideDelegation = false, hideChrome = false, forcedSubgroup, apiBase = '/api/v1/config', fieldPermissions },
  ref
) {
  const [config, setConfig] = useState<Record<string, unknown>>({})
  const [fields, setFields] = useState<ConfigField[]>([])
  const [categories, setCategories] = useState<ConfigSubgroup[]>([])
  const [permissions, setPermissions] = useState<Record<string, PermissionState>>({})
  // hr2-v2 (K4/E6): HR-Rollen-Delegation aus der Config-Response (ct='hr') —
  // gepufferte Drafts landen im selben Dirty-Save wie Werte und Scopes.
  const [hrDelegation, setHrDelegation] = useState<{ role: string; permissions: Record<string, { visible: boolean; editable: boolean }> } | null>(null)
  const [delegationDrafts, setDelegationDrafts] = useState<Record<string, { visible: boolean; editable: boolean }>>({})
  // Direktorkonfig v2 (D11/E6): per-Direktor visible/editable aus der
  // Director-Config-Response (nur gesetzt, wenn ein Direktor liest).
  const [serverFieldPermissions, setServerFieldPermissions] = useState<Record<string, { visible: boolean; editable: boolean }> | null>(null)
  const [activeSubgroup, setActiveSubgroup] = useState('')
  const [dirty, setDirty] = useState<Set<string>>(new Set())
  const [permSaving, setPermSaving] = useState(false)
  const [loading, setLoading] = useState(true)
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})
  const [zoneConfig, setZoneConfig] = useState<RadarZones | null>(null)
  const [zoneConfigDirty, setZoneConfigDirty] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [jsonErrors, setJsonErrors] = useState<Record<string, string>>({})

  // Track dirty state as boolean to avoid useEffect re-firing on every render
  // (hr2-v2: HR-Delegation-Drafts zählen mit in den Dirty-Zustand, E2)
  const isDirty = useMemo(
    () => dirty.size > 0 || Object.keys(delegationDrafts).length > 0,
    [dirty.size, delegationDrafts],
  )

  // Ref to hold onDirtyChange callback to avoid useEffect re-firing
  const onDirtyChangeRef = useRef(onDirtyChange)
  useEffect(() => {
    onDirtyChangeRef.current = onDirtyChange
  }, [onDirtyChange])

  // Sync dirty state to parent component.
  // Uses isDirty (boolean from useMemo) to avoid re-firing on every render.
  // Uses ref to avoid re-firing when onDirtyChange reference changes.
  // This useEffect is necessary to avoid calling onDirtyChange
  // inside a state updater function, which would trigger a React warning.
  useEffect(() => {
    if (isDirty) {
      onDirtyChangeRef.current?.(true, dirty)
    }
  }, [isDirty])

  const ct = configType || 'substitution_request'

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setConfig({})
    setFields([])
    setCategories([])
    setServerFieldPermissions(null)
    setHrDelegation(null)
    setDelegationDrafts({})
    setDirty(new Set())
    setPermissions({})
    setZoneConfig(null)
    setZoneConfigDirty(false)
    setActiveSubgroup('')
    setValidationErrors({})
    setJsonErrors({})
    onError?.(null)
    apiClient.get(`${apiBase}/${ct}`)
      .then(res => {
        if (cancelled) return
        const data = res.data
        setConfig(data.config)
        setFields(data.fields)
        setCategories(data.categories)
        setServerFieldPermissions(data.field_permissions ?? null)
        setHrDelegation(data.delegation ?? null)
        if (data.categories[0]) setActiveSubgroup(data.categories[0].name)
        if (ct === 'radar' && data.config.zone_config) {
          setZoneConfig(data.config.zone_config as RadarZones)
          setZoneConfigDirty(false)
        }
        onConfigLoaded?.(data.label || ct.replace(/_/g, ' '), data.description || '')
      })
      .catch(() => {
        if (!cancelled) onError?.('Konfiguration konnte nicht geladen werden')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    // hr2-v2 (K4): HR-Delegation kommt über die Config-Response — der
    // Lehrer-Permissions-Endpunkt bleibt für hr ungenutzt (I3-Altlast).
    if (!hideDelegation && ct !== 'hr') {
      apiClient.get(`${apiBase}/${ct}/permissions`)
        .then(res => {
          if (!cancelled) setPermissions(res.data?.permissions || {})
        })
        .catch(() => { /* permissions are optional */ })
    }
    return () => { cancelled = true }
  }, [ct, apiBase, hideDelegation, onConfigLoaded, onError])

  useEffect(() => {
    if (forcedSubgroup && categories.some(c => c.name === forcedSubgroup)) {
      setActiveSubgroup(forcedSubgroup)
    }
  }, [forcedSubgroup, categories])

  // hr2-v2 (K4): HR-Modus — Delegation steuert die HR-Rolle (E6), nie die
  // Lehrer-/Direktor-Lesefilterung.
  const isHrMode = ct === 'hr' && hrDelegation?.role === 'hr'

  const hrPerm = (key: string): { visible: boolean; editable: boolean } | undefined =>
    delegationDrafts[key] ?? hrDelegation?.permissions?.[key]

  // Effektive per-Direktor-Rechte: Prop hat Vorrang, sonst Response-Werte.
  const effectiveFieldPerms = useMemo(
    () => fieldPermissions ?? serverFieldPermissions,
    [fieldPermissions, serverFieldPermissions],
  )

  const fieldVisible = (key: string): boolean =>
    !effectiveFieldPerms || effectiveFieldPerms[key]?.visible !== false

  const fieldEditable = (key: string): boolean =>
    !effectiveFieldPerms || effectiveFieldPerms[key]?.editable !== false

  /** Update permissions from the merged map returned by PUT (avoids a refetch). */
  const applyMergedPermissions = (merged: Record<string, PermissionState>) => {
    setPermissions(merged)
  }

  const defByKey = useMemo(() => {
    const map: Record<string, ConfigField> = {}
    for (const f of fields) map[f.key] = f
    return map
  }, [fields])

  const setVal = (key: string, value: unknown) => {
    setConfig(prev => ({ ...prev, [key]: value }))
    setDirty(prev => {
      const next = new Set(prev)
      next.add(key)
      return next
    })
    setValidationErrors(prev => { const n = { ...prev }; delete n[key]; return n })
  }

  const handleToggle = (key: string, value: boolean) => {
    if (!value && CRITICAL_KEYS.includes(key)) {
      if (!window.confirm(`Warnung: Wenn Sie "${key.replace(/_/g, ' ')}" deaktivieren, können Lehrpersonen dieses Feature nicht mehr nutzen.\n\nForfahren?`)) return
    }
    setVal(key, value)
  }

  const handleSave = useCallback(async () => {
    if (dirty.size === 0 && Object.keys(delegationDrafts).length === 0 && !zoneConfigDirty) return
    onError?.(null)
    try {
      const data: Record<string, unknown> = {}
      // hr2-v2 (B4/K5): Scope-Felder reisen nie im Werte-Payload — ihr
      // Objekt {module_active, self_activation} geht in den scopes-Teil.
      const scopes: Record<string, unknown> = {}
      for (const k of dirty) {
        const def = defByKey[k]
        if (def?.type === 'password' && (config[k] === '' || config[k] === null || config[k] === undefined)) {
          continue
        }
        // Design logo is persisted immediately via its own upload/delete endpoints
        // and must never travel through the generic PUT payload (base64 blob).
        if (ct === 'design' && k === 'design_logo_data') {
          continue
        }
        // Direktorkonfig v2 (E2): custom-Felder persistieren nie via Config-PUT
        if (def?.type === 'custom') {
          continue
        }
        if (def?.type === 'scope') {
          scopes[k] = config[k]
          continue
        }
        data[k] = config[k]
      }
      // hr2-v2 (E6): gepufferte HR-Delegation — setting-Scopes als
      // visible/editable-Patches in denselben Save-Roundtrip (E15).
      if (isHrMode) {
        for (const [key, draft] of Object.entries(delegationDrafts)) {
          scopes[`setting.${key}`] = draft
        }
      }
      if (ct === 'radar' && zoneConfigDirty) {
        data['zone_config'] = zoneConfig
      }
      const response = await apiClient.put(`${apiBase}/${ct}`, {
        data,
        ...(ct === 'hr' && Object.keys(scopes).length > 0 ? { scopes } : {}),
      })
      // hr2-v3: PUT /config/hr liefert {config, delegation} (wie GET).
      // Fallback: flaches Dict (ältere Mocks / Alt-Clients).
      if (ct === 'hr' && response?.data && typeof response.data === 'object') {
        const body = response.data as Record<string, unknown>
        const nested = body.config
        const wrapped = nested && typeof nested === 'object' && !Array.isArray(nested)
        const configSlice = wrapped
          ? nested as Record<string, unknown>
          : Object.fromEntries(Object.entries(body).filter(([k]) => k !== 'delegation'))
        setConfig(prev => ({ ...prev, ...configSlice }))
        const del = body.delegation as { role: string; permissions: Record<string, { visible: boolean; editable: boolean }> } | undefined
        if (del && typeof del === 'object' && del.permissions) {
          setHrDelegation(del)
        } else if (isHrMode && hrDelegation) {
          setHrDelegation({
            ...hrDelegation,
            permissions: { ...hrDelegation.permissions, ...delegationDrafts },
          })
        }
      }
      onSuccess?.()
      setDirty(new Set())
      setDelegationDrafts({})
      setZoneConfigDirty(false)
      onDirtyChange?.(false, new Set())
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Speichern fehlgeschlagen'
      onError?.(msg)
      const match = msg.match(/'([^']+)'/)
      if (match && match[1]) setValidationErrors({ [match[1]]: msg })
    }
  }, [dirty, delegationDrafts, zoneConfigDirty, config, defByKey, ct, zoneConfig, apiBase, onError, onSuccess, onDirtyChange, isHrMode, hrDelegation])

  const handleReset = useCallback(() => {
    if ((dirty.size > 0 || Object.keys(delegationDrafts).length > 0 || zoneConfigDirty) && window.confirm('Alle ungespeicherten Änderungen verwerfen?')) {
      setLoading(true)
      setDelegationDrafts({})
      apiClient.get(`${apiBase}/${ct}`)
        .then(res => {
          const data = res.data
          setConfig(data.config)
          setDirty(new Set())
          setValidationErrors({})
          setHrDelegation(data.delegation ?? null)
          if (ct === 'radar' && data.config.zone_config) {
            setZoneConfig(data.config.zone_config as RadarZones)
            setZoneConfigDirty(false)
          }
          onDirtyChange?.(false, new Set())
        })
        .finally(() => setLoading(false))
    }
  }, [dirty, delegationDrafts, zoneConfigDirty, ct, apiBase, onDirtyChange])

  useImperativeHandle(ref, () => ({
    save: handleSave,
    reset: handleReset,
    isDirty: () => dirty.size > 0 || Object.keys(delegationDrafts).length > 0 || zoneConfigDirty,
  }), [dirty, delegationDrafts, zoneConfigDirty, handleSave, handleReset])

  const setModeOrder = (idx: number, mode: string) => {
    const allowed = new Set(['automatic', 'priority'])
    const cur = ((config['default_mode_priority_order'] as string[]) || ['automatic', 'priority'])
      .filter(m => allowed.has(m))
    const next = [...cur]
    if (allowed.has(mode)) next[idx] = mode
    setVal('default_mode_priority_order', next)
  }

  const handleDefaultModeChange = (mode: string) => {
    const allowed = new Set(['automatic', 'priority'])
    if (!allowed.has(mode)) return
    setVal('default_request_mode', mode)
  }

  /** Apply a design preset: fills all color/theme fields and the preset key (dirty-marked). */
  const applyDesignPreset = (presetKey: string, values: import('@/utils/designPresets').DesignPresetValues) => {
    setVal('design_color_preset', presetKey)
    for (const [key, value] of Object.entries(values)) {
      setVal(key, value)
    }
  }

  /** Color change on a design color field: mark preset as 'custom' when it deviates from the active preset. */
  const handleDesignColorChange = (key: string, value: string) => {
    setVal(key, value)
    const activePreset = getPreset(String(config.design_color_preset || ''))
    if (activePreset) {
      const presetValue = (activePreset.values as unknown as Record<string, unknown>)[key]
      if (presetValue !== value) {
        setVal('design_color_preset', 'custom')
      }
    } else if (config.design_color_preset !== 'custom') {
      setVal('design_color_preset', 'custom')
    }
  }

  /** Logo persisted via dedicated endpoint — refresh value without marking dirty. */
  const handleDesignLogoChanged = (logoData: string) => {
    setConfig(prev => ({ ...prev, design_logo_data: logoData }))
  }

  type DelegationState = 'versteckt' | 'sichtbar' | 'aenderbar' | 'gemischt' | 'none'

  const cardState = (cardKeys: string[]): DelegationState => {
    // hr2-v2 (K4/E6): im HR-Modus beschreibt der setting.*-Scope-Zustand
    // (plus gepufferte Drafts) den Karten-Zustand — nicht die Lehrer-Permissions.
    if (isHrMode) {
      const keys = cardKeys.filter(k => hrDelegation?.permissions?.[k] !== undefined || delegationDrafts[k] !== undefined)
      if (keys.length === 0) return 'none'
      const states = keys.map(k => {
        const p = hrPerm(k) ?? { visible: false, editable: false }
        if (!p.visible) return 'versteckt'
        return p.editable ? 'aenderbar' : 'sichtbar'
      })
      const uniq = Array.from(new Set(states))
      return uniq.length === 1 ? (uniq[0] as DelegationState) : 'gemischt'
    }
    const delegatable = cardKeys.filter(k => defByKey[k] && defByKey[k].delegatable !== false)
    if (delegatable.length === 0) return 'none'
    const states = delegatable.map(k => {
      const p = permissions[k] || { visible: false, editable: false, min_value: null, max_value: null }
      if (!p.visible) return 'versteckt'
      return p.editable ? 'aenderbar' : 'sichtbar'
    })
    const uniq = Array.from(new Set(states))
    return uniq.length === 1 ? (uniq[0] as DelegationState) : 'gemischt'
  }

  const applyDelegation = async (cardKeys: string[], target: 'versteckt' | 'sichtbar' | 'aenderbar') => {
    // hr2-v2 (E2/E6): gepufferte HR-Delegation — kein API-Call, kein Confirm
    // (über «Verwerfen» revidierbar); der Save schreibt Werte+Scopes gemeinsam.
    if (isHrMode) {
      const next: Record<string, { visible: boolean; editable: boolean }> = { ...delegationDrafts }
      for (const k of cardKeys) {
        if (hrDelegation?.permissions?.[k] === undefined && delegationDrafts[k] === undefined) continue
        next[k] = {
          visible: target !== 'versteckt',
          editable: target === 'aenderbar',
        }
      }
      setDelegationDrafts(next)
      return
    }
    if (!window.confirm(`Delegation für alle Settings in dieser Karte auf "${target}" setzen? Bestehende Bereiche (min/max) bleiben erhalten.`)) return
    const delegatable = cardKeys.filter(k => defByKey[k] && defByKey[k].delegatable !== false)
    if (delegatable.length === 0) return
    // Include the field's known min/max; omit entirely when unknown locally so
    // the backend keeps stored ranges (explicit null would CLEAR them).
    const payload: Record<string, { visible: boolean; editable: boolean; min_value?: number | null; max_value?: number | null }> = {}
    for (const k of delegatable) {
      const p = permissions[k]
      payload[k] = {
        visible: target !== 'versteckt',
        editable: target === 'aenderbar',
        ...(p ? { min_value: p.min_value, max_value: p.max_value } : {}),
      }
    }
    setPermSaving(true)
    try {
      const res = await configApi.updatePermissions(ct, payload)
      applyMergedPermissions(res.permissions)
    } catch (err: unknown) {
      onError?.(err instanceof Error ? err.message : 'Delegation fehlgeschlagen')
    } finally {
      setPermSaving(false)
    }
  }

  const DEPENDENCIES: Record<string, string> = {
    'affinity_bonus_same_class': 'affinity_bonus_enabled',
    'affinity_bonus_same_subject': 'affinity_bonus_enabled',
    'affinity_bonus_same_subject_year': 'affinity_bonus_enabled',
    'affinity_bonus_taught_before': 'affinity_bonus_enabled',
    'max_multi_week_lessons': 'multi_week_packages_enabled',
  }

  const isDisabled = (key: string): boolean => {
    const parentKey = DEPENDENCIES[key]
    if (!parentKey) return false
    return !asBool(config[parentKey])
  }

  const matchesSearch = (key: string): boolean => {
    if (!searchQuery.trim()) return true
    const def = defByKey[key]
    if (!def) return false
    const q = searchQuery.toLowerCase()
    return key.toLowerCase().includes(q) || def.label.toLowerCase().includes(q) || def.description.toLowerCase().includes(q)
  }

  const STRUCTURED_LIST_SCHEMAS: Record<string, StructuredListField[]> = {
    director_document_types: [
      { key: 'id', label: 'ID', type: 'string' },
      { key: 'name', label: 'Name', type: 'string' },
      { key: 'is_active', label: 'Aktiv', type: 'boolean' },
      { key: 'sort_order', label: 'Sortierung', type: 'number' },
    ],
    director_rejection_reasons: [
      { key: 'id', label: 'ID', type: 'string' },
      { key: 'reason', label: 'Grund', type: 'string' },
      { key: 'is_active', label: 'Aktiv', type: 'boolean' },
      { key: 'sort_order', label: 'Sortierung', type: 'number' },
    ],
    teacher_code_examples: [
      { key: 'name', label: 'Name', type: 'string' },
      { key: 'code', label: 'Code', type: 'string' },
    ],
  }

  const renderField = (key: string) => {
    const def = defByKey[key]
    if (!def) return null
    const val = config[key]
    const isCritical = CRITICAL_KEYS.includes(key)
    // v3-Fix (E6-Lücke): renderField hat ein eigenes `disabled` — auch hier
    // die per-Direktor-Bearbeitbarkeit berücksichtigen, sonst liessen sich
    // nicht-editierbare Felder über das Feld-Control doch ändern.
    const disabled = isDisabled(key) || !fieldEditable(key)

    // Dedicated editor for the school-wide radar default filter (filter-fix-v2).
    if (key === 'radar_filter_default_state') {
      return (
        <RadarFilterDefaultEditor
          value={val}
          onChange={(v) => setVal(key, v)}
        />
      )
    }

    // Konzept A — User-Multi-Select für die "kein Vertreter"-Empfängerliste
    if (key === 'no_substitute_recipient_user_ids') {
      return (
        <UserMultiSelect
          value={Array.isArray(val) ? val : []}
          onChange={(v) => setVal(key, v)}
          disabled={disabled}
        />
      )
    }

    // Direktorkonfig v2 (6.1): Abwesenheitsarten-Verwaltung — eigene Tabelle
    // mit Sofort-Speichern pro Aktion (ersetzt DocumentRulesEditor).
    // (`disabled` enthält bereits !fieldEditable, vgl. renderField.)
    if (key === 'absence_types') {
      return <AbsenceTypeEditor disabled={disabled} />
    }

    // hr2-v2 (E5/E7): Feldart 'scope' — eine Zeile, zwei App-Switches
    // («Aktiv» + «Selbst-Aktivierung erlauben»). Master OFF (Draft-Wert!)
    // disabled beide Switches; die Karte zeigt zusätzlich den Badge
    // «Master inaktiv» (B4-Präzedenz, Muster «Parent deaktiviert»).
    if (def.type === 'scope') {
      const raw = (val && typeof val === 'object' ? val : {}) as { module_active?: unknown; self_activation?: unknown }
      const moduleActive = asBool(raw.module_active)
      const selfActivation = asBool(raw.self_activation)
      const masterOff = ct === 'hr' && !asBool(config['hr_module_enabled'])
      const switchBtn = (on: boolean, onChange: (v: boolean) => void, label: string) => (
        <label className={`flex items-center gap-1.5 ${masterOff ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
          <button
            onClick={() => { if (!masterOff && !disabled) onChange(!on) }}
            disabled={disabled || masterOff}
            role="switch" aria-checked={on} aria-label={`${def.label}: ${label}`}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 ${on ? 'bg-indigo-600' : 'bg-gray-300'} ${disabled || masterOff ? 'cursor-not-allowed' : ''}`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${on ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>
          <span className="text-xs text-gray-600">{label}</span>
        </label>
      )
      return (
        <div className="flex items-center gap-4 flex-wrap">
          {switchBtn(moduleActive, v => setVal(key, { ...raw, module_active: v }), 'Aktiv')}
          {switchBtn(selfActivation, v => setVal(key, { ...raw, self_activation: v }), 'Selbst-Aktivierung erlauben')}
        </div>
      )
    }
    // hr2-v2 (E18): enum_list — validierte Chip-Auswahl statt freiem TagInput
    if (def.type === 'enum_list') {
      const listValue = Array.isArray(val) ? (val as string[]) : []
      const choices = def.choices ?? []
      const labelOf = (c: string) => def.choice_labels?.[c] ?? c.replace(/_/g, ' ')
      const toggle = (c: string) => {
        const next = listValue.includes(c) ? listValue.filter(x => x !== c) : [...listValue, c]
        setVal(key, next)
      }
      return (
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1.5">
            {choices.map(c => {
              const on = listValue.includes(c)
              return (
                <button
                  key={c} type="button"
                  onClick={() => toggle(c)}
                  disabled={disabled}
                  className={`px-2.5 py-1 text-xs rounded-full border transition ${
                    on ? 'bg-indigo-50 border-indigo-300 text-indigo-700 font-medium'
                      : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400'
                  } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {labelOf(c)}
                </button>
              )
            })}
          </div>
          {listValue.length === 0 && <span className="text-xs text-gray-400">(leer = alle)</span>}
        </div>
      )
    }
    if (def.type === 'bool') {
      const on = asBool(val)
      return (
        <button
          onClick={() => isCritical ? handleToggle(key, !on) : setVal(key, !on)}
          disabled={disabled}
          role="switch" aria-checked={on} aria-label={def.label}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 ${on ? 'bg-indigo-600' : 'bg-gray-300'} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${on ? 'translate-x-6' : 'translate-x-1'}`} />
        </button>
      )
    }
    if (key === 'feature_flag_rollout') {
      const n = typeof val === 'number' ? val : Number(val) || 0
      return (
        <div className="flex items-center gap-3 w-48">
          <input type="range" value={n} min={0} max={100}
            onChange={e => setVal(key, Number(e.target.value))}
            disabled={disabled}
            className="flex-1 accent-indigo-600" />
          <span className="text-sm font-semibold text-indigo-600 w-12 text-right">{n}%</span>
        </div>
      )
    }
    if (def.type === 'int') {
      const n = typeof val === 'number' ? val : Number(val) || 0
      return (
        <input
          type="number" value={n}
          min={def.min_value ?? undefined} max={def.max_value ?? undefined}
          onChange={e => setVal(key, Number(e.target.value))}
          disabled={disabled}
          className={`w-24 px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-50' : ''}`}
        />
      )
    }
    if (def.type === 'float') {
      const minV = def.min_value ?? -Infinity
      const maxV = def.max_value ?? Infinity
      const isInvalid = typeof val === 'number' && (val < minV || val > maxV)
      return (
        <FloatInput
          value={typeof val === 'number' ? val : null}
          onChange={v => setVal(key, v ?? 0)}
          placeholder="z.B. 0.25"
          disabled={disabled}
          className={`w-24 px-2 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${isInvalid ? 'border-red-300 bg-red-50' : 'border-gray-300'} ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-50' : ''}`}
        />
      )
    }
    if (def.type === 'password') {
      const isMasked = val === PASSWORD_MASK
      return (
        <input
          type="password" value={isMasked ? '' : String(val || '')}
          onChange={e => setVal(key, e.target.value)}
          disabled={disabled}
          placeholder={`${PASSWORD_MASK} (leer = unverändert)`}
          className={`w-64 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-50' : ''}`}
        />
      )
    }
    if (def.type === 'color') {
      const isDesignColor = ct === 'design'
      return (
        <ColorField
          value={String(val || '#000000')}
          onChange={(c) => (isDesignColor ? handleDesignColorChange(key, c) : setVal(key, c))}
          label={def.label}
          description={def.description}
          embedded
        />
      )
    }
    // School design special widgets
    if (ct === 'design' && key === 'design_color_preset') {
      return (
        <DesignPresetPicker
          value={String(val || 'blue')}
          onApplyPreset={applyDesignPreset}
        />
      )
    }
    if (ct === 'design' && key === 'design_logo_data') {
      return (
        <DesignLogoUpload
          value={String(val || '')}
          onChanged={handleDesignLogoChanged}
          onError={msg => onError?.(msg)}
        />
      )
    }
    if (ct === 'design' && key === 'login_page_slug') {
      const slugValue = String(val || '')
      const publicUrl = slugValue ? `${window.location.origin}/login/s/${slugValue}` : ''
      return (
        <div className="w-full max-w-md space-y-1.5">
          <input
            type="text"
            value={slugValue}
            onChange={e => setVal(key, e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
            placeholder="z.B. gymnovodemo"
            className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 font-mono"
          />
          {publicUrl && (
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <a href={publicUrl} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline truncate">{publicUrl}</a>
              <button
                type="button"
                onClick={() => void navigator.clipboard?.writeText(publicUrl)}
                className="px-2 py-0.5 text-xs border border-gray-300 rounded hover:bg-gray-50 shrink-0"
              >
                Kopieren
              </button>
            </div>
          )}
        </div>
      )
    }
    if (def.type === 'json_list' && STRUCTURED_LIST_SCHEMAS[key]) {
      const schema = STRUCTURED_LIST_SCHEMAS[key]
      const listValue = Array.isArray(val) ? val : []
      return (
        <div className="w-full max-w-2xl">
          <StructuredListEditor
            value={listValue as Array<Record<string, unknown>>}
            onChange={(newValue) => setVal(key, newValue)}
            schema={schema}
            disabled={disabled}
          />
        </div>
      )
    }
    if (def.type === 'role_state') {
      const labels: Record<string, string> = {
        versteckt: '🔒 Verborgen',
        sichtbar: '👁 Sichtbar',
        aenderbar: '✏️ Änderbar',
      }
      const states = (def.choices?.length ? def.choices : ['versteckt', 'sichtbar']).map(id => [id, labels[id] || id] as const)
      const current = states.some(([id]) => id === val) ? val : 'versteckt'
      return (
        <div className={`flex items-center gap-0.5 p-0.5 bg-gray-100 rounded-lg ${disabled ? 'opacity-50 pointer-events-none' : ''}`}>
          {states.map(([id, label]) => (
            <button
              key={id}
              type="button"
              onClick={() => setVal(key, id)}
              className={`px-2.5 py-1 text-xs rounded-md transition ${current === id ? 'bg-white shadow-sm font-semibold text-gray-800' : 'text-gray-500 hover:text-gray-700'}`}
            >
              {label}
            </button>
          ))}
        </div>
      )
    }
    if (def.type === 'str' && def.choices) {
      // hr2-v2 (E14): deutsche Labels mit Fallback auf den Roh-Wert
      const choiceLabel = (c: string) => def.choice_labels?.[c] ?? c.replace(/_/g, ' ')
      if (key === 'default_request_mode') {
        return (
          <select value={displayValue(val, '')} onChange={e => handleDefaultModeChange(e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-indigo-500">
            {def.choices.map(c => <option key={c} value={c}>{choiceLabel(c)}</option>)}
          </select>
        )
      }
      return (
        <select value={displayValue(val, '')} onChange={e => setVal(key, e.target.value)}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-indigo-500">
          {def.choices.map(c => <option key={c} value={c}>{choiceLabel(c)}</option>)}
        </select>
      )
    }
    if (def.type === 'json_list' && def.choices) {
      const isModeOrder = key === 'default_mode_priority_order'
      const allowed = isModeOrder ? new Set(['automatic', 'priority']) : null
      const raw = (Array.isArray(val) ? val : (def.default as string[])) as string[]
      const order = allowed ? raw.filter(m => allowed.has(m)) : raw
      const labels: Record<string, string> = { automatic: 'Automatisch', priority: 'Priorität' }
      const slots = isModeOrder ? 2 : Math.max(order.length, 1)
      const choiceList = allowed ? def.choices.filter(c => allowed.has(c)) : def.choices
      return (
        <div className="flex items-center gap-2 flex-wrap">
          {Array.from({ length: slots }, (_, idx) => (
            <div key={idx} className="flex items-center gap-1">
              <span className="text-xs text-gray-400">{idx + 1}.</span>
              <select value={order[idx] || ''} onChange={e => setModeOrder(idx, e.target.value)}
                className="px-2 py-1 text-xs border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-indigo-500">
                {choiceList.map(c => <option key={c} value={c}>{labels[c] || c}</option>)}
              </select>
            </div>
          ))}
        </div>
      )
    }
    if (def.type === 'str') {
      return (
        <input
          type="text"
          value={val === null || val === undefined ? '' : String(val)}
          onChange={e => setVal(key, e.target.value)}
          disabled={disabled}
          placeholder={def.default ? String(def.default) : '—'}
          className={`w-64 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-50' : ''}`}
        />
      )
    }
    if (def.type === 'json_list' && !STRUCTURED_LIST_SCHEMAS[key]) {
      const listValue = Array.isArray(val) ? val : (def.default as unknown[]) || []
      const isSimpleArray = listValue.every(item => typeof item === 'string' || typeof item === 'number')
      if (isSimpleArray) {
        return (
          <div className="w-full max-w-md">
            <TagInput
              value={listValue as (string | number)[]}
              onChange={(newValue) => setVal(key, newValue)}
              disabled={disabled}
              placeholder="Wert hinzufuegen..."
            />
          </div>
        )
      }
      const textValue = JSON.stringify(listValue, null, 2)
      return (
        <div className="w-full max-w-2xl">
          <textarea
            value={textValue}
            onChange={e => {
              try {
                const parsed = JSON.parse(e.target.value)
                if (Array.isArray(parsed)) {
                  setVal(key, parsed)
                  setJsonErrors(prev => { const n = { ...prev }; delete n[key]; return n })
                } else {
                  setJsonErrors(prev => ({ ...prev, [key]: 'Muss ein Array sein' }))
                }
              } catch {
                setJsonErrors(prev => ({ ...prev, [key]: 'Ungueltiges JSON' }))
              }
            }}
            disabled={disabled}
            rows={3}
            className={`w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 font-mono ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-50' : ''}`}
          />
          <p className="text-xs text-gray-500 mt-1">JSON-Array eingeben (z.B. [1, 2, 3])</p>
          {jsonErrors[key] && <p className="text-xs text-red-600 mt-1">{jsonErrors[key]}</p>}
        </div>
      )
    }
    if (val === null || val === undefined) {
      return <span className="text-sm text-gray-400 italic">Nicht gesetzt</span>
    }
    if (Array.isArray(val)) {
      return <span className="text-sm text-gray-600">{val.length > 0 ? val.join(', ') : '—'}</span>
    }
    return <span className="text-sm text-gray-600">{displayValue(val)}</span>
  }

  const isStackedField = (key: string): boolean => {
    if (STACKED_FIELD_KEYS.has(key)) return true
    const def = defByKey[key]
    if (!def) return false
    if (STRUCTURED_LIST_SCHEMAS[key]) return true
    // TagInput/textarea editors are wide panels: stacking keeps the description
    // full-width instead of being squeezed into a narrow column next to them.
    if (def.type === 'json_list' && !def.choices) return true
    return false
  }

  const renderSettingRow = (key: string) => {
    const def = defByKey[key]
    if (!def) return null
    if (!matchesSearch(key)) return null
    // Direktorkonfig v2 (E6): nicht sichtbare Felder (per-Direktor-Recht) weg
    if (!fieldVisible(key)) return null
    const vErr = validationErrors[key]
    const jErr = jsonErrors[key]
    // Reparatur-Fix C9: Ursachen trennen — «Parent deaktiviert» (Abhängigkeit)
    // und «Kein Bearbeitungsrecht» (per-Direktor-Berechtigung) sind
    // unterschiedliche Erklärungen und dürfen nicht in einem Badge landen.
    // (Die Feld-Controls selbst berechnen ihr disabled in renderField.)
    const parentDisabled = isDisabled(key)
    const isRadarToggle = ct === 'radar' && def.type === 'bool' && def.default === true
    const isDelegatable = def.delegatable !== false
    const stacked = isStackedField(key)

    // hr2-v2 (K4/E6): HR-Delegation — gepuffertes Control mit Ziel-Rolle HR
    // für alle Felder mit setting.*-Scope; Scope-Zeilen und Master bleiben
    // System-Einstellungen.
    const delegation = !hideDelegation && (
      isHrMode ? (
        hrPerm(key) !== undefined || delegationDrafts[key] !== undefined ? (
          <FieldDelegationControl
            configType={ct}
            fieldKey={key}
            fieldType={def.type}
            registryMin={def.min_value}
            registryMax={def.max_value}
            perm={undefined}
            onSaved={() => { /* buffered mode never calls this */ }}
            onError={msg => onError?.(msg)}
            mode="buffered"
            roleLabel="HR"
            bufferedPerm={hrPerm(key)}
            onBufferedChange={next => setDelegationDrafts(prev => ({ ...prev, [key]: next }))}
          />
        ) : (
          <span
            className="p-1.5 text-gray-300 border-l border-gray-200 pl-3 ml-1"
            title="System-Einstellung — wird nicht an HR delegiert"
          >🔒</span>
        )
      ) : (
        isDelegatable ? (
          <FieldDelegationControl
            configType={ct}
            fieldKey={key}
            fieldType={def.type}
            registryMin={def.min_value}
            registryMax={def.max_value}
            perm={permissions[key]}
            onSaved={applyMergedPermissions}
            onError={msg => onError?.(msg)}
          />
        ) : (
          <span
            className="p-1.5 text-gray-300 border-l border-gray-200 pl-3 ml-1"
            title="Kann nicht an Lehrpersonen delegiert werden"
          >🔒</span>
        )
      )
    )

    const labelRow = (
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-medium text-gray-800">{def.label}</span>
        {isRadarToggle && (
          <span className="text-xs text-green-700 bg-green-50 border border-green-200 px-1.5 py-0.5 rounded">
            Empfohlen: Aktiviert
          </span>
        )}
        {parentDisabled && (
          <span className="text-xs text-orange-600 bg-orange-50 px-1.5 py-0.5 rounded">Parent deaktiviert</span>
        )}
        {!parentDisabled && !fieldEditable(key) && (
          <span className="text-xs text-gray-600 bg-gray-100 border border-gray-200 px-1.5 py-0.5 rounded">Kein Bearbeitungsrecht</span>
        )}
      </div>
    )

    const description = def.description && <p className="text-xs text-gray-500 mt-0.5">{def.description}</p>
    const errors = (
      <>
        {vErr && <p className="text-xs text-red-600 mt-0.5">{vErr}</p>}
        {jErr && <p className="text-xs text-red-600 mt-0.5">{jErr}</p>}
      </>
    )

    if (stacked) {
      return (
        <div key={key} className="py-3 border-b border-gray-100 last:border-b-0">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">{labelRow}</div>
            {delegation}
          </div>
          {description}
          {errors}
          <div className="mt-3">{renderField(key)}</div>
        </div>
      )
    }

    return (
      <div key={key} className="flex items-start justify-between gap-4 py-3 border-b border-gray-100 last:border-b-0">
        <div className="flex-1 min-w-0">
          {labelRow}
          {description}
          {errors}
        </div>
        <div className="flex-shrink-0 flex items-start gap-2">
          <div className="flex items-center gap-2">{renderField(key)}</div>
          {delegation}
        </div>
      </div>
    )
  }

  const renderCard = (sg: ConfigSubgroup) => {
    // Direktorkonfig v2 (E6): Karte weg, wenn alle Felder per-Recht unsichtbar
    const visibleKeys = sg.keys.filter(fieldVisible)
    if (visibleKeys.length === 0) return null
    const state = cardState(sg.keys)
    // hr2-v2 (E7): Master-OFF (Draft-Wert) — Scope-Karten ausgegraut + Badge
    // statt Status-Kasten; B4-Präzedenz gilt serverseitig ohnehin.
    const hrMasterOff = ct === 'hr' && !asBool(config['hr_module_enabled'])
    const isScopeCard = sg.keys.some(k => defByKey[k]?.type === 'scope')
    return (
      <div key={sg.name} data-subgroup={sg.name} className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-base font-semibold text-gray-900">{sg.leitfrage || sg.name}</h2>
              {hrMasterOff && isScopeCard && (
                <span className="text-xs text-orange-600 bg-orange-50 px-1.5 py-0.5 rounded">Master inaktiv</span>
              )}
            </div>
            {sg.intro && <p className="text-sm text-gray-500 mt-1">{sg.intro}</p>}
          </div>
          {!hideDelegation && state !== 'none' && (
            <div className={`flex items-center gap-0.5 p-0.5 bg-gray-100 rounded-lg ${permSaving ? 'opacity-60 pointer-events-none' : ''}`}>
              {([['versteckt', '🔒 Versteckt'], ['sichtbar', '👁 Sichtbar'], ['aenderbar', '✏️ Änderbar']] as const).map(([id, label]) => (
                <button key={id} onClick={() => applyDelegation(sg.keys, id)} title={label}
                  className={`px-2.5 py-1 text-xs rounded-md transition ${state === id ? 'bg-white shadow-sm font-semibold text-gray-800' : 'text-gray-500 hover:text-gray-700'}`}>
                  {label}
                </button>
              ))}
              {state === 'gemischt' && <span className="px-2 text-xs text-gray-400">gemischt</span>}
            </div>
          )}
          {!hideDelegation && state === 'none' && <span className="text-xs text-gray-400 bg-gray-50 border border-gray-200 px-2 py-1 rounded">System-Einstellungen</span>}
        </div>
        <div className="mt-2">{sg.keys.map(renderSettingRow)}</div>

        {ct === 'design' && sg.name === 'Farben & Theme' && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <DesignPreview config={config} />
          </div>
        )}

        {ct === 'radar' && sg.name === 'Allgemeine Einstellungen' && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h3 className="text-base font-semibold text-gray-900 mb-3">Zonen-Konfiguration</h3>
            <p className="text-sm text-gray-500 mb-4">
              Konfiguration der Radar-Zonen (Panic/Middle/Outer). Definiert Schwellen, Farben und Labels für die Dringlichkeits-Anzeige.
            </p>
            <RadarZoneEditor
              value={zoneConfig}
              onChange={(zones) => {
                setZoneConfig(zones)
                setZoneConfigDirty(true)
                onDirtyChange?.(true, dirty)
              }}
            />
          </div>
        )}

        {!hideDelegation && ct === 'director' && sg.name === 'Stellvertretungen' && (
          <DirectorDelegationEditor />
        )}

        {/* Direktorkonfig v2 (D12/D16): Superadmin steuert die Rechte der
            Direktoren pro Karte/Feld — hier in der Abwesenheitsarten-Karte. */}
        {!hideDelegation && ct === 'director' && sg.name === 'Abwesenheitsarten' && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <DirectorPermissionsEditor fieldKey="absence_types" fieldLabel="Abwesenheitsarten verwalten" />
          </div>
        )}
      </div>
    )
  }

  if (loading) return <div className="p-6 text-gray-500">Laden...</div>

  if (hideChrome) {
    const sg = categories.find(s => s.name === (forcedSubgroup || activeSubgroup)) || categories[0]
    return <div className="max-w-3xl mx-auto">{sg ? renderCard(sg) : null}</div>
  }

  // Direktorkonfig v2 (E6): alle Karten gefiltert (per-Direktor-Rechte) →
  // Leerzustand statt leerer Seite
  if (categories.length === 0) {
    return (
      <div className="p-6 text-sm text-gray-500">
        Keine sichtbaren Einstellungen — der Superadmin hat keine Karten für Sie freigegeben.
      </div>
    )
  }

  return (
    <>
      {/* Search bar */}
      <div className="px-4 lg:px-6 py-2 border-b bg-white">
        <input
          type="text"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder="Einstellung suchen..."
          className="w-full max-w-md px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
        />
      </div>

      {/* Body: Sidebar + Content */}
      <div className="flex flex-1 min-h-0">
        <aside className="hidden lg:block w-72 border-r bg-gray-50/60 flex-shrink-0 overflow-y-auto p-3 space-y-0.5">
          {!searchQuery && categories.map(sg => (
            <button key={sg.name} onClick={() => setActiveSubgroup(sg.name)}
              className={`w-full text-left px-3 py-2 rounded-md text-sm transition ${activeSubgroup === sg.name ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-gray-600 hover:bg-gray-100'}`}>
              {sg.leitfrage || sg.name}
            </button>
          ))}
        </aside>

        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          <div className="max-w-3xl mx-auto">
            {searchQuery ? (
              <div>{categories.map(renderCard)}</div>
            ) : (
              <>
                <div className="lg:hidden">{categories.map(renderCard)}</div>
                <div className="hidden lg:block">
                  {(() => {
                    const sg = categories.find(s => s.name === activeSubgroup) || categories[0]
                    return sg ? renderCard(sg) : null
                  })()}
                </div>
              </>
            )}
          </div>
        </main>
      </div>
    </>
  )
})
